/**
 * MessageActionMenu — Long-press / right-click context menu for messages
 *
 * Adapted from bachata-nation-next MessageActionMenu.tsx.
 * Neubrutalism popover with emoji bar + action list.
 */

import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/features/auth';
import type { ChatMessage } from '../types';

interface MessageActionMenuProps {
  message: ChatMessage;
  position: { x: number; y: number };
  onClose: () => void;
  onReply?: (message: ChatMessage) => void;
  onEdit?: (messageId: string, content: string) => void;
  onDelete?: (messageId: string) => void;
  onStar?: (messageId: string, action: 'add' | 'remove') => void;
  onReaction?: (messageId: string, emoji: string, action: 'add' | 'remove') => void;
  onForward?: (message: ChatMessage) => void;
  onPin?: (messageId: string, action: 'pin' | 'unpin') => void;
  onInfo?: (message: ChatMessage) => void;
  onCopy?: (content: string) => void;
  isAdmin?: boolean;
}

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export const MessageActionMenu = memo(function MessageActionMenu({
  message,
  position,
  onClose,
  onReply,
  onEdit,
  onDelete,
  onStar,
  onReaction,
  onForward,
  onPin,
  onInfo,
  onCopy,
  isAdmin,
}: MessageActionMenuProps) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const menuRef = useRef<HTMLDivElement>(null);
  const isMine = message.senderId === currentUserId;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Position the menu within viewport after measuring actual size
  const [adjustedPos, setAdjustedPos] = useState({ x: position.x, y: position.y });

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    const pad = 8;
    let x = position.x;
    let y = position.y;

    // Clamp horizontal: prefer showing to the right of click, flip left if overflowing
    if (x + rect.width + pad > window.innerWidth) {
      x = Math.max(pad, window.innerWidth - rect.width - pad);
    }
    // Clamp vertical: prefer showing below click, flip up if overflowing
    if (y + rect.height + pad > window.innerHeight) {
      y = Math.max(pad, window.innerHeight - rect.height - pad);
    }

    setAdjustedPos({ x, y });
  }, [position.x, position.y]);

  const style: React.CSSProperties = {
    position: 'fixed',
    top: adjustedPos.y,
    left: adjustedPos.x,
    zIndex: 200,
  };

  const handleCopy = useCallback(() => {
    if (message.content) {
      if (onCopy) {
        onCopy(message.content);
      } else {
        navigator.clipboard.writeText(message.content);
      }
    }
    onClose();
  }, [message.content, onCopy, onClose]);

  return createPortal(
    <>
      {/* Backdrop (invisible, just catches outside clicks) */}
      <div className="fixed inset-0 z-[199]" onClick={onClose} />

      {/* Menu */}
      <div
        ref={menuRef}
        style={{ ...style, borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}
        className="z-[200] min-w-[180px] animate-in fade-in zoom-in-95 rounded-3xl overflow-hidden border shadow-sm glass-surface"
      >
        {/* Quick emoji bar */}
        {!message.isDeleted && (
          <div className="flex items-center justify-center gap-1 border-b-[2px] px-3 py-2" style={{ borderColor: 'var(--user-page-card-border)' }}>
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onReaction?.(message.id, emoji, 'add');
                  onClose();
                }}
                className="rounded-full p-1 text-lg transition-transform hover:scale-125 active:scale-95"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Actions list */}
        <div className="py-1">
          {/* Reply */}
          {!message.isDeleted && onReply && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 17 4 12 9 7" />
                  <path d="M20 18v-2a4 4 0 00-4-4H4" />
                </svg>
              }
              label={t('messaging.reply')}
              onClick={() => {
                onReply(message);
                onClose();
              }}
            />
          )}

          {/* Copy */}
          {message.content && !message.isDeleted && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              }
              label={t('messaging.copy')}
              onClick={handleCopy}
            />
          )}

          {/* Forward */}
          {!message.isDeleted && onForward && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 17 20 12 15 7" />
                  <path d="M4 18v-2a4 4 0 014-4h12" />
                </svg>
              }
              label={t('messaging.forward.title')}
              onClick={() => {
                onForward(message);
                onClose();
              }}
            />
          )}

          {/* Star / Unstar */}
          {!message.isDeleted && onStar && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill={message.isStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              }
              label={message.isStarred ? t('messaging.unstar') : t('messaging.star')}
              onClick={() => {
                onStar(message.id, message.isStarred ? 'remove' : 'add');
                onClose();
              }}
            />
          )}

          {/* Pin / Unpin (admin only) */}
          {!message.isDeleted && onPin && isAdmin && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill={message.isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 17v5" />
                  <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1 1 1 0 0 1 1 1z" />
                </svg>
              }
              label={message.isPinned ? t('messaging.unpin') : t('messaging.pin')}
              onClick={() => {
                onPin(message.id, message.isPinned ? 'unpin' : 'pin');
                onClose();
              }}
            />
          )}

          {/* Info */}
          {!message.isDeleted && onInfo && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              }
              label={t('messaging.messageInfo.title')}
              onClick={() => {
                onInfo(message);
                onClose();
              }}
            />
          )}

          {/* Edit (own messages only) */}
          {isMine && !message.isDeleted && message.type === 'text' && onEdit && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              }
              label={t('messaging.edit')}
              onClick={() => {
                const newContent = prompt(t('messaging.editMessage'), message.content || '');
                if (newContent && newContent !== message.content) {
                  onEdit(message.id, newContent);
                }
                onClose();
              }}
            />
          )}

          {/* Delete (own messages only) */}
          {isMine && !message.isDeleted && onDelete && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              }
              label={t('messaging.delete')}
              onClick={() => {
                onDelete(message.id);
                onClose();
              }}
              danger
            />
          )}
        </div>
      </div>
    </>,
    document.body
  );
});

interface MenuActionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}

function MenuAction({ icon, label, onClick, danger }: MenuActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
        danger
          ? 'text-error-600 hover:bg-error-50'
          : ''
      }`}
      style={danger ? undefined : { color: 'var(--user-page-heading)' }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export default MessageActionMenu;
